import {
  ContentSwitcher,
  IconButton,
  Scrollbar,
  type PlaybackTransport,
} from "@carbon6/c6-frontend-design";
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { DatasetStructureChannel, EpisodeSummary } from "@/api";
import {
  actionSourceSegmentsState,
  datasetHasActionSource,
  type ActionSourceSegmentsState,
} from "@/api/diagnosticSegments";
import { RefreshIcon } from "@/components/icons";
import {
  PLAYBACK_TIMELINE_ROW,
  usePlayerViewport,
  type PlayerTimelineTrack,
  type PlayerViewportSource,
} from "@/components/video";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatNumber } from "@/utils";
import { useEpisodeReviewSavedEpisodeRevision } from "../episodeReviewSavedStore";
import type { LoadState } from "../loadState";
import { ChannelBandsView } from "./ChannelBandsView";
import { DiagnosticsSummaryLine } from "./DiagnosticsSummaryLine";
import type { CollectionBandState, EpisodeDiagnosticDetail } from "./diagnosticsSeries";
import { diagnosticSeriesStatusText, episodeGridStatusText } from "./diagnosticsStatusText";
import {
  diagnosticsViewOptions,
  resolveDiagnosticsView,
  type DiagnosticsViewId,
} from "./diagnosticsView";
import { MotionSeriesView } from "./motion/MotionSeriesView";
import { MotionSummary } from "./motion/MotionSummary";
import type { EpisodeTimelineResult } from "./motion/timelineArtifact";
import { useEpisodeTimeline } from "./motion/useEpisodeTimeline";
import { buildChannelIndex, type ChannelStructureIndex } from "./parquetChannels";
import { buildActionSourceModel, type ActionSourceModel } from "./segments/actionSourceSegments";
import { FreezeCandidateChips } from "./segments/FreezeCandidateChips";
import { freezeCandidateSpans, type QualityCandidateSpan } from "./segments/qualityCandidates";
import { SegmentChipRow } from "./segments/SegmentChipRow";
import { bandShading, phaseToSeconds } from "./segments/segmentShading";
import {
  actionSourceTimelineTracks,
  freezeCandidateTimelineTrack,
} from "./segments/segmentTimeline";
import { SegmentsView } from "./segments/SegmentsView";
import { TimingView } from "./TimingView";
import { useEpisodeDiagnosticDetail } from "./useEpisodeDiagnosticDetail";
import type { EpisodeDiagnosticsState } from "./useEpisodeDiagnostics";
import styles from "./EpisodeDiagnosticsSurface.module.css";

// 구조를 아직 로드하지 않았을 때 쓰는 안정 참조다. 렌더마다 새 배열을 만들면 색인 memo 가 매번 무효가 된다.
const EMPTY_CHANNELS: readonly DatasetStructureChannel[] = [];
const NO_CANDIDATES: readonly QualityCandidateSpan[] = [];

/**
 * 영상 쪽이 전달하는 재생 핸들. 이 표면은 영상에 대해 다음 네 가지만 한다:
 * - 재생 위치 옮기기
 * - 재생 위치 읽기
 * - 타임라인 행 등록하기
 * - 재생 바 바로 위에 칩 줄 두기
 *
 * 영상 컴포넌트를 import 하지 않으려고 모양만 정의한다.
 */
export type DiagnosticsPlayback = {
  seekToSeconds: (seconds: number) => void;
  transport: PlaybackTransport | null;
  setTimelineTracks: (episodeUuid: string, tracks: readonly PlayerTimelineTrack[] | null) => void;
  viewport: PlayerViewportSource;
  // 칩 줄을 둘 자리(재생 바 바로 위). 생략하면 칩 줄을 표면 머리에 그린다. `null` 이면 자리가
  // 아직 붙지 않은 상태라서 그리지 않는다. 칩은 재생 바의 행과 같은 시간을 가리킨다.
  // 그래서 칩 줄을 바에 붙인다.
  transportLead?: HTMLElement | null;
};

export type EpisodeDiagnosticsSurfaceProps = {
  // 진단 상세·action_source 구간·타임라인 행·칩을 켠다(리뷰·분석 두 탭).
  enabled?: boolean;
  // 분석 탭은 진단 본문을 표시한다. 리뷰 탭은 접을 수 있는 그리퍼 보기만 표시한다.
  // 생략하면 `enabled` 값을 쓴다.
  bodyEnabled?: boolean;
  episode: EpisodeSummary;
  band?: CollectionBandState | null;
  // 컬렉션 단위로 1회 적재한 진단 행 색인. 상위 컴포넌트가 주입한다. null 은 미조회를 뜻하며,
  // 그때는 그 값들을 뺀다.
  episodeDiagnostics?: EpisodeDiagnosticsState | null;
  // 데이터셋 구조 채널. 그룹과 표시명의 SoT 이고, action_source 채널 유무의 근거다.
  // undefined 는 "아직 모름"을 뜻한다. 채널이 없다는 뜻이 아니다.
  structureChannels?: readonly DatasetStructureChannel[];
  // 영상이 없는 자리(테스트 등)에서는 생략한다. 그때 그래프와 칩은 읽기 전용이다.
  playback?: DiagnosticsPlayback;
  // 고른 보기. null 이면 기본 보기다. 창(에피소드 탭)이 이 값을 보관한다.
  // 그래서 다른 에피소드로 넘어가도 값이 남는다.
  view: DiagnosticsViewId | null;
  onViewChange: (view: DiagnosticsViewId) => void;
};

/**
 * 영상 옆 진단 표면. 요약 한 줄, 보기 전환, 고른 보기를 표시한다. 구간 칩은 재생 바 바로 위에
 * 표시한다.
 *
 * 진단 상세는 두 단계로 온다:
 * - 첫 응답(상태·채널 사실·값 구간)이 오면 칩·요약·구간 보기를 표시한다.
 * - 위상 그리드가 오면 채널 밴드 보기를 표시한다.
 *
 * 구간은 영상 타임라인에도 출처 행으로 등록한다. 영상 정지 후보가 있으면 후보 행과 후보 칩도
 * 함께 둔다. FileBrowser 가 `renderEpisodeExtra` 슬롯으로 이 표면을 주입한다. 칩과 행은 본문을
 * 감춘 탭(리뷰)에서도 보여야 한다. 그래서 이 표면이 칩과 행의 단일 소유자이고, 두 탭에 모두
 * 마운트된다(`enabled` / `bodyEnabled`).
 */
export function EpisodeDiagnosticsSurface({
  enabled = true,
  bodyEnabled = enabled,
  episode,
  band = null,
  episodeDiagnostics = null,
  structureChannels,
  playback,
  view,
  onViewChange,
}: EpisodeDiagnosticsSurfaceProps): ReactNode {
  const [grippersOpen, setGrippersOpen] = useState(false);
  const grippersId = useId();
  // 상세는 칩과 행에 필요하므로 두 탭 모두 받는다. 위상 그리드는 본문이 보일 때만 받는다.
  const bodyActive = enabled && bodyEnabled;
  // 이 에피소드의 검토를 저장하면 상세만 다시 받는다. 검토 완료가 품질 후보 확인을 기록하므로
  // 칩의 확인 표시가 바로 바뀐다. 다음 에피소드로 넘어간 경우에는 새 에피소드를 받으므로 무관하다.
  const reviewSaved = useEpisodeReviewSavedEpisodeRevision(episode.dataset_uuid, episode.uuid);
  const { detail, grid, reload } = useEpisodeDiagnosticDetail(
    episode.dataset_uuid,
    episode.uuid,
    enabled,
    bodyActive,
    reviewSaved,
  );
  const channelIndex = useMemo(
    () => buildChannelIndex(structureChannels ?? EMPTY_CHANNELS),
    [structureChannels],
  );
  const hasActionSource = datasetHasActionSource(structureChannels);
  const detailData = detail.status === "success" ? detail.data : null;
  const segmentsState = useMemo(
    () =>
      detailData === null ? null : actionSourceSegmentsState(detailData.segments, hasActionSource),
    [detailData, hasActionSource],
  );
  const model = useMemo(
    () => (segmentsState?.kind === "ready" ? buildActionSourceModel(segmentsState.channel) : null),
    [segmentsState],
  );
  const candidateSpans = useMemo(
    () =>
      detailData === null ? NO_CANDIDATES : freezeCandidateSpans(detailData.qualityCandidates),
    [detailData],
  );
  // 재생 바는 등록한 행 배열을 통째로 바꾼다. 그래서 출처 행과 후보 행을 여기서 한 배열로 만든다.
  // 출처 행이 없으면 기본 재생 행을 맨 위에 둔다. 그래서 바의 첫 줄이 에피소드마다 같다.
  const tracks = useMemo(() => {
    const sourceRows =
      model === null || model.bands.length === 0 ? null : actionSourceTimelineTracks(model);
    if (candidateSpans.length === 0) {
      return sourceRows;
    }
    return [
      ...(sourceRows ?? [PLAYBACK_TIMELINE_ROW]),
      freezeCandidateTimelineTrack(candidateSpans),
    ];
  }, [model, candidateSpans]);

  // 출처·후보 행을 영상 타임라인에 등록하고, 언마운트할 때 제거한다. 의존성은 핸들 함수만 둔다.
  // 재생 핸들 객체는 transport 가 바뀔 때마다 새로 오지만, 등록하는 행은 같다.
  const setTimelineTracks = playback?.setTimelineTracks;
  const episodeUuid = episode.uuid;
  useEffect(() => {
    if (setTimelineTracks === undefined) return;
    setTimelineTracks(episodeUuid, tracks);
    return () => setTimelineTracks(episodeUuid, null);
  }, [setTimelineTracks, episodeUuid, tracks]);

  const options = useMemo(() => diagnosticsViewOptions(hasActionSource), [hasActionSource]);
  const activeView = resolveDiagnosticsView(view, hasActionSource);
  // 요약 통계 본문(3번째 RTT, 창원 ep26 실측 145 KB)은 **표면이** 보관한다. 운동 밴드 보기는
  // 언마운트된다. 그래서 훅을 보기 안에 두면 보기를 바꿀 때마다 본문을 다시 받는다.
  const timeline = useEpisodeTimeline(detailData, bodyActive && activeView === "motionBands");
  // 운동 보기를 **보고 있을 때만** 창을 구독한다. 이 보기는 감춰도 마운트된 상태로 남는다
  // (아래 `hidden inert`). 구독을 유지하면, 다른 진단 보기를 보는 동안에도 타임라인을 끌 때마다
  // 캔버스 십수 장을 다시 그린다.
  const motionActive = enabled && (bodyEnabled ? activeView === "motion" : grippersOpen);
  const motionViewport = usePlayerViewport(motionActive ? (playback?.viewport ?? null) : null);
  const seekToSeconds = playback?.seekToSeconds;
  const duration = episode.duration_seconds;
  const parquet = episode.parquet;
  // 사람이 낸 구간을 채널 밴드 뒤 배경 위치로 변환한다. 길이를 모르면 변환할 수 없으므로 빈 목록이다.
  const shading = useMemo(
    () => (model === null ? [] : bandShading(model.bands, duration ?? null)),
    [model, duration],
  );

  const sourceChips =
    model !== null && model.bands.length > 0 ? (
      <SegmentChipRow
        model={model}
        transport={playback?.transport ?? null}
        onSeekToSeconds={seekToSeconds}
      />
    ) : null;
  const candidateChips =
    candidateSpans.length > 0 ? (
      <FreezeCandidateChips
        spans={candidateSpans}
        transport={playback?.transport ?? null}
        onSeekToSeconds={seekToSeconds}
      />
    ) : null;
  // 출처 칩 뒤에 세로선을 두고 후보 칩을 잇는다. 두 칩 모두 재생 바의 자기 행과 같은 시간을 가리킨다.
  const chipRow =
    sourceChips === null && candidateChips === null ? null : (
      <div className={styles.chipGroups}>
        {sourceChips}
        {sourceChips !== null && candidateChips !== null ? (
          <span className={styles.chipDivider} aria-hidden="true" />
        ) : null}
        {candidateChips}
      </div>
    );
  const lead = playback?.transportLead;
  // 재생 바 위 자리에서는 상세를 받는 동안 칩 한 줄 높이를 비워 둔다. 그렇지 않으면 에피소드를
  // 바꿀 때마다 칩 줄이 접혔다 펼쳐지고, 그 위의 리뷰 독이 위아래로 움직인다.
  const chipPending = enabled && hasActionSource && detail.status === "loading";

  return (
    <div
      className={styles.surface}
      data-view={bodyEnabled ? activeView : "gripper"}
      data-grippers-open={!bodyEnabled && grippersOpen ? "true" : undefined}
    >
      {lead === undefined ? (
        chipRow === null ? null : (
          <div className={styles.chips}>{chipRow}</div>
        )
      ) : lead === null ? null : (
        createPortal(
          chipRow ??
            (chipPending ? <div className={styles.chipsPending} aria-hidden="true" /> : null),
          lead,
        )
      )}
      {bodyEnabled ? (
        <>
          <DiagnosticsSummaryLine
            episode={episode}
            episodeDiagnostics={episodeDiagnostics}
            actionSource={model}
          />
          {parquet !== null && parquet.failed_files > 0 ? (
            <div className={styles.notice}>
              파일 {formatNumber(parquet.failed_files)}개를 읽지 못해 행 수는 나머지 파일만 반영한
              값입니다.
            </div>
          ) : null}
          <div className={styles.switcherRow}>
            <ContentSwitcher
              label="진단 보기"
              size="sm"
              options={options}
              value={activeView}
              onValueChange={(value) => {
                const next = options.find((option) => option.value === value);
                if (next !== undefined) onViewChange(next.value);
              }}
            />
          </div>
        </>
      ) : (
        <div>
          <IconButton
            size="sm"
            variant="surface"
            text={grippersOpen ? "그리퍼 접기" : "그리퍼 펼치기"}
            aria-expanded={grippersOpen}
            aria-controls={grippersId}
            onClick={() => setGrippersOpen((open) => !open)}
          />
        </div>
      )}
      <Scrollbar
        id={grippersId}
        className={styles.viewScroll}
        gutter="auto"
        aria-label={bodyEnabled ? "진단 상세 스크롤" : "그리퍼 상세 스크롤"}
        hidden={!bodyEnabled && !grippersOpen}
        inert={!bodyEnabled && !grippersOpen}
      >
        {!bodyEnabled ? null : activeView === "segments" ? (
          <SegmentsPanel
            detail={detail}
            state={segmentsState}
            model={model}
            onSeekToSeconds={seekToSeconds}
            onRefresh={reload}
          />
        ) : activeView === "channelBands" ? (
          <ChannelBandsView
            episodeUuid={episode.uuid}
            grid={grid}
            band={band}
            episodeDiagnostics={episodeDiagnostics}
            channels={channelIndex}
            // 그래프 가로축은 0~1 위상이다. 초로 변환하려면 에피소드 길이가 필요하다. 길이를
            // 모르면(미산출) 이동과 재생선을 연결하지 않는다. 틀린 지점을 가리키는 것보다 낫다.
            onSeekToPhase={
              seekToSeconds !== undefined && duration != null
                ? (phase) => seekToSeconds(phaseToSeconds(phase, duration))
                : undefined
            }
            onRefresh={reload}
            shading={shading}
            transport={playback?.transport ?? null}
            durationSeconds={duration ?? null}
          />
        ) : activeView === "timing" ? (
          // 스위처에 타이밍 옵션이 없다(2026-09-12, `diagnosticsView.ts`). 그래서 지금은 이 경우에
          // 도달하지 않는다. 옵션을 다시 추가하면 이 경우를 그대로 쓴다. 그래서 지우지 않는다.
          <TimingPanel detail={detail} index={channelIndex} onRefresh={reload} />
        ) : activeView === "motionBands" ? (
          <MotionBandsPanel
            detail={detail}
            timeline={timeline}
            channels={channelIndex}
            onRefresh={reload}
          />
        ) : null}
        <div className={styles.motion} hidden={!motionActive} inert={!motionActive}>
          <MotionSeriesView
            key={episode.uuid}
            detail={detailData}
            onRefresh={reload}
            detailError={
              !motionActive
                ? undefined
                : detail.status === "error"
                  ? detail.message
                  : detail.status === "success" && detail.data === null
                    ? "진단 데이터를 찾을 수 없습니다. 다시 확인해 주세요."
                    : undefined
            }
            enabled={motionActive}
            gripperOnly={!bodyEnabled}
            channels={channelIndex}
            transport={playback?.transport ?? null}
            viewport={motionViewport}
            onSeekToSeconds={seekToSeconds}
          />
        </div>
      </Scrollbar>
    </div>
  );
}

// 구간 보기의 빈 자리 문구. 빈 목록의 뜻(`ActionSourceSegmentsState`)마다 다른 문구를 쓴다.
const SEGMENTS_STATE_TEXT: Record<Exclude<ActionSourceSegmentsState["kind"], "ready">, string> = {
  pending:
    "이 에피소드의 action_source 구간이 아직 준비되지 않았습니다. 재진단이 끝나면 여기에 나타납니다.",
  unknown: "이 에피소드에는 표시할 action_source 구간이 없습니다.",
  none: "이 Dataset에는 action_source 채널이 없어 구간을 나눌 수 없습니다.",
};

function SegmentsPanel({
  detail,
  state,
  model,
  onSeekToSeconds,
  onRefresh,
}: {
  detail: LoadState<EpisodeDiagnosticDetail | null>;
  state: ActionSourceSegmentsState | null;
  model: ActionSourceModel | null;
  onSeekToSeconds?: (seconds: number) => void;
  // 진단 상세를 다시 받는다.
  onRefresh: () => void;
}): ReactNode {
  const retryAction = { label: "다시 시도", onClick: onRefresh, icon: <RefreshIcon /> };
  if (detail.status === "idle" || detail.status === "loading") {
    return <LoadingState>action_source 구간을 불러오고 있습니다.</LoadingState>;
  }
  if (detail.status === "error") {
    return (
      <EmptyState tone="error" action={retryAction}>
        {detail.message}
      </EmptyState>
    );
  }
  if (detail.data === null || state === null) {
    return (
      <EmptyState tone="error" action={retryAction}>
        진단 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
      </EmptyState>
    );
  }
  if (state.kind !== "ready") {
    return (
      <EmptyState
        // 재진단이 끝나면 구간이 생긴다. 그래서 준비 전 상태에만 새로고침을 둔다.
        action={
          state.kind === "pending"
            ? { label: "새로고침", onClick: onRefresh, icon: <RefreshIcon /> }
            : undefined
        }
      >
        {SEGMENTS_STATE_TEXT[state.kind]}
      </EmptyState>
    );
  }
  if (model === null || model.bands.length === 0) {
    return <EmptyState>구간의 시각을 읽지 못해 영상에 맞출 수 없습니다.</EmptyState>;
  }
  return <SegmentsView model={model} onSeekToSeconds={onSeekToSeconds} />;
}

/**
 * 운동 밴드 보기. 저장된 요약 통계를 표시한다.
 *
 * **진단 상세를 먼저 확인한다.** 상세가 없으면 타임라인 훅의 key 가 null 이고 훅은 `idle` 을
 * 돌려준다. 그래서 타임라인 상태만 보면, 404 일 때 아무 안내 없는 빈 화면이 된다.
 *
 * 이 보기는 운동 개요·상세 산출물을 읽지 않는다. 그래서 시간별 곡선이 미산출이어도 표시된다.
 */
function MotionBandsPanel({
  detail,
  timeline,
  channels,
  onRefresh,
}: {
  detail: LoadState<EpisodeDiagnosticDetail | null>;
  timeline: LoadState<EpisodeTimelineResult>;
  channels: ChannelStructureIndex;
  // 진단 상세를 다시 받는다. 타임라인은 새 상세를 보고 자기 데이터도 다시 받는다.
  onRefresh: () => void;
}): ReactNode {
  const refreshAction = { label: "새로고침", onClick: onRefresh, icon: <RefreshIcon /> };
  const retryAction = { label: "다시 시도", onClick: onRefresh, icon: <RefreshIcon /> };
  if (detail.status === "idle" || detail.status === "loading") {
    return <LoadingState>요약 통계를 불러오고 있습니다.</LoadingState>;
  }
  if (detail.status === "error") {
    return (
      <EmptyState tone="error" action={retryAction}>
        {detail.message}
      </EmptyState>
    );
  }
  if (detail.data === null) {
    return (
      <EmptyState tone="error" action={retryAction}>
        진단 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
      </EmptyState>
    );
  }
  if (timeline.status === "error") {
    return (
      <EmptyState tone="error" action={retryAction}>
        {timeline.message}
      </EmptyState>
    );
  }
  if (timeline.status !== "success") {
    return <LoadingState>요약 통계를 불러오고 있습니다.</LoadingState>;
  }
  if (timeline.data.kind === "status") {
    return (
      <EmptyState action={refreshAction}>
        {diagnosticSeriesStatusText(timeline.data.series, "요약 통계")}
      </EmptyState>
    );
  }
  if (timeline.data.kind !== "timeline") {
    return <EmptyState action={refreshAction}>저장된 요약 통계가 없습니다.</EmptyState>;
  }
  return (
    <div className={styles.viewBody}>
      <MotionSummary timeline={timeline.data.timeline} channels={channels} />
    </div>
  );
}

function TimingPanel({
  detail,
  index,
  onRefresh,
}: {
  detail: LoadState<EpisodeDiagnosticDetail | null>;
  index: ChannelStructureIndex;
  // 진단 상세를 다시 받는다.
  onRefresh: () => void;
}): ReactNode {
  const retryAction = { label: "다시 시도", onClick: onRefresh, icon: <RefreshIcon /> };
  if (detail.status === "idle" || detail.status === "loading") {
    return <LoadingState>채널 정보를 불러오고 있습니다.</LoadingState>;
  }
  if (detail.status === "error") {
    return (
      <EmptyState tone="error" action={retryAction}>
        {detail.message}
      </EmptyState>
    );
  }
  if (detail.data === null) {
    return (
      <EmptyState tone="error" action={retryAction}>
        진단 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
      </EmptyState>
    );
  }
  if (detail.data.channels.length === 0) {
    return detail.data.status === "ready" ? (
      <EmptyState>표시할 채널 정보가 없습니다.</EmptyState>
    ) : (
      <EmptyState action={{ label: "새로고침", onClick: onRefresh, icon: <RefreshIcon /> }}>
        {episodeGridStatusText(detail.data)}
      </EmptyState>
    );
  }
  return <TimingView channels={detail.data.channels} index={index} />;
}
